import { motion } from 'framer-motion';
import { Target, Heart, TrendingUp, Users, Crosshair, Lightbulb, Layers } from 'lucide-react';

const AboutPage = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const values = [
    {
      icon: <Crosshair size={32} />,
      title: 'Radical Specificity',
      description: 'In a world of generalists, the specialist wins. By narrowing your focus to a specific niche, you create the space to become the undisputed authority in your field.'
    },
    {
      icon: <Heart size={32} />,
      title: 'Empathy-Led Problem Solving',
      description: 'True innovation happens when you stop guessing what people want and start solving the actual pain points that your customers are begging you to solve.'
    },
    {
      icon: <Layers size={32} />,
      title: 'Scalable Integrity',
      description: 'We believe in building robust systems and processes that allow your business to expand its impact while maintaining the high standard of excellence your customers expect.'
    },
    {
      icon: <Lightbulb size={32} />,
      title: 'Practical Clarity',
      description: 'Our goal is to strip away the "hustle culture" noise and provide clear, actionable strategies. If a solution isn\'t easy to understand and simple to implement, it isn\'t the right solution.'
    }
  ];

  return (
    <div data-testid="about-page" className="pt-20">
      {/* Hero Section */}
      <section className="section-padding">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeInUp} className="text-center mb-8">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold" data-testid="about-hero-title">
              About The Servant CEO
            </h1>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <img
                src="https://customer-assets.emergentagent.com/job_growth-guide-21/artifacts/4n7x2qrv_Gemini_Generated_Image_mb7feemb7feemb7f.png"
                alt="Will Owen - The Servant CEO"
                className="rounded-2xl shadow-2xl"
                data-testid="about-image"
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="space-y-6"
            >
              <h2 className="text-3xl sm:text-4xl font-bold" data-testid="about-story-title">
                Meet Will Owen
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed">
                Will Owen isn't just building businesses; he's obsessed with building the right ones. With a career defined by strategic growth and a "customer-first" obsession, Will has spent years refining the blueprint for sustainable success. He believes that the most successful entrepreneurs aren't the ones with the loudest voices, but the ones who listen the most intently to their market.
              </p>
              <p className="text-lg text-muted-foreground leading-relaxed">
                His latest work is a distillation of that philosophy, centered on a high-impact three-step framework: Serve, Solve, and Scale. Will's mission is to help entrepreneurs cut through the noise of "hustle culture" by identifying a specific niche, diagnosing their deepest pain points, and building the systems necessary to transform a single solution into a thriving enterprise.
              </p>
              <p className="text-lg text-muted-foreground leading-relaxed">
                When he isn't helping founders find their "onlyness" in a crowded market, Will is likely exploring new ways to simplify the complex world of business strategy. He writes for the entrepreneur who is tired of guessing and ready to build a business that provides genuine value—and scales because of it.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="section-padding bg-secondary/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6" data-testid="values-title">
              Core Values
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              These principles guide everything I do and teach.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                data-testid={`value-card-${index}`}
                className="glass-card rounded-2xl p-8 text-center hover:-translate-y-2 transition-all duration-300"
              >
                <div className="w-16 h-16 rounded-full bg-accent/20 flex items-center justify-center mx-auto mb-6 text-accent">
                  {value.icon}
                </div>
                <h3 className="text-xl font-bold mb-4">{value.title}</h3>
                <p className="text-muted-foreground leading-relaxed">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission Statement */}
      <section className="section-padding">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="glass-card rounded-2xl p-12 text-center"
          >
            <h2 className="text-3xl sm:text-4xl font-bold mb-6" data-testid="mission-title">
              My Mission
            </h2>
            <p className="text-lg sm:text-xl text-muted-foreground leading-relaxed">
              To empower entrepreneurs to build high-impact, customer-first businesses that serve their niche, solve deep pain points, and scale with purpose.
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;